import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Receipt } from "@phosphor-icons/react";

/**
 * Settings → Refund section.
 *
 * Behavior:
 * - Hits GET /api/refunds/eligibility on mount.
 * - If a refund is pending → show the pending state.
 * - If no charge in the 24h window → show ineligibility note.
 * - Otherwise show charge details + a "Request refund" textarea/CTA.
 * - Always shows a small history of recent refund requests below.
 */
export default function RefundSection() {
  const [elig, setElig] = useState(null);
  const [history, setHistory] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: e }, { data: h }] = await Promise.all([
        api.get("/refunds/eligibility"),
        api.get("/refunds/mine"),
      ]);
      setElig(e);
      setHistory(h || []);
    } catch (err) {
      // Not fatal — section just won't render below.
      setElig({ eligible: false, reason: "unavailable" });
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (reason.trim().length < 10) {
      toast.error("Please share at least a sentence of context (min 10 characters).");
      return;
    }
    if (!window.confirm(
      "If approved, your premium access will be revoked immediately and any " +
      "active recurring subscription will be canceled — you won't be charged again.\n\n" +
      "Continue?"
    )) return;
    setBusy(true);
    try {
      await api.post("/refunds/request", { reason: reason.trim() });
      toast.success("Refund request submitted — an admin will respond within 24 hours.");
      setReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit refund request.");
    } finally {
      setBusy(false);
    }
  };

  if (!elig) return null;

  return (
    <div className="border border-zinc-200 bg-white p-6 mt-6" data-testid="refund-section">
      <div className="flex items-center gap-2">
        <Receipt size={18} weight="bold" className="text-zinc-700" />
        <h2 className="font-display font-bold text-xl tracking-tight">Refunds</h2>
      </div>
      <p className="text-sm text-zinc-600 mt-2">
        Charges are refundable within <strong>24 hours</strong> of purchase. Submit a request and we&apos;ll respond promptly.
      </p>
      <div className="mt-3 border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        <strong>What happens if approved:</strong> your premium access is revoked
        immediately and any active recurring subscription is canceled, so you won&apos;t
        be charged again. You keep full access while your request is under review.
      </div>

      {elig.reason === "already_pending" && (
        <div data-testid="refund-pending-state" className="mt-4 border border-amber-300 bg-amber-50 p-4 text-sm">
          You have a refund request <strong>under review</strong>. We&apos;ll email you as soon as it&apos;s processed.
        </div>
      )}

      {elig.eligible && (
        <div data-testid="refund-eligible-state" className="mt-4 space-y-3">
          <div className="border border-zinc-200 bg-zinc-50 p-3 text-sm font-mono text-zinc-700">
            ${elig.charge.amount_usd.toFixed(2)} {elig.charge.currency?.toUpperCase()} · charged {new Date(elig.charge.created_at).toLocaleString()}
          </div>
          <Textarea
            data-testid="refund-reason-input"
            placeholder="Briefly tell us what's wrong so we can improve."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="gf-sharp border-zinc-300 bg-white text-sm min-h-[100px]"
          />
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="refund-submit-btn"
            className="gf-sharp bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide"
          >
            {busy ? "Submitting…" : "Request refund"}
          </Button>
        </div>
      )}

      {!elig.eligible && elig.reason !== "already_pending" && (
        <div data-testid="refund-ineligible-state" className="mt-4 text-sm text-zinc-500 font-mono">
          No charge within the past 24 hours.
          {elig.reason === "unavailable" ? " (Service temporarily unavailable.)" : ""}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-6">
          <div className="gf-overline">Recent requests</div>
          <div className="mt-2 space-y-2">
            {history.map((r) => (
              <div
                key={r.id}
                data-testid={`refund-history-${r.id}`}
                className="border border-zinc-200 bg-white p-3 text-xs font-mono flex items-center justify-between"
              >
                <span>
                  ${r.amount_usd.toFixed(2)} · {new Date(r.created_at).toLocaleDateString()}
                </span>
                <span className={
                  r.status === "approved" ? "text-emerald-700 font-bold uppercase tracking-wide"
                  : r.status === "rejected" ? "text-red-700 font-bold uppercase tracking-wide"
                  : "text-amber-700 font-bold uppercase tracking-wide"
                }>
                  {r.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
